import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/src/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClassSlotWithDetail } from '@repo/shared-types';
import { useSubjects } from '../../hooks/useSubjects';
import { useBranches } from '../../hooks/useBranches';
import { useCreateSlot, useUpdateSlot } from '../../hooks/useSlots';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useMembers } from '../../hooks/useTeam';
import { useSubscription } from '../../hooks/useSubscription';
import { useCan } from '../../hooks/useCan';
import { useAuthStore } from '../../stores/authStore';
import { TeacherListEditor, type TeacherListRow } from '../../components/TeacherListEditor';

// Radix `SelectItem` cannot take `value=""`, so "no branch" needs a sentinel.
const NO_BRANCH = '__none__';

export function SlotFormDialog({
  open, onOpenChange, onCreated, slot,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: (slot: ClassSlotWithDetail) => void;
  /** When provided, the dialog edits this existing slot instead of creating a new one. */
  slot?: ClassSlotWithDetail;
}) {
  const { t } = useTranslation('scheduling');
  const { data: subjects } = useSubjects();
  // includeArchived, because a slot may still be ATTACHED to a cơ sở that was
  // archived after it was created. Archived branches are not offered as
  // choices — only the slot's OWN archived branch is rendered, disabled, so
  // the trigger shows where the class actually is instead of going blank and
  // silently detaching it on the next save (THI-137 final review F2).
  const { data: branchList } = useBranches({ includeArchived: true });
  const allBranches = branchList?.branches;
  const isEdit = !!slot;
  const create = useCreateSlot();
  const update = useUpdateSlot(slot?.id ?? '');
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  const { data: members } = useMembers();
  const { data: subscription } = useSubscription();
  const canManageMembers = useCan('manage:members');
  const authUser = useAuthStore((s) => s.user);
  // Initial values come from `slot` when editing. The parent remounts this component (via a
  // `key` keyed on the slot id) whenever it opens for a (possibly different) slot, so these
  // lazy initializers are enough to prefill — no effect needed to resync on open.
  const [subjectId, setSubjectId] = useState(slot?.subjectId ?? '');
  const [branchId, setBranchId] = useState(slot?.branchId ?? '');
  const [dayOfWeek, setDayOfWeek] = useState(String(slot?.dayOfWeek ?? 2));
  const [startTime, setStartTime] = useState(slot?.startTime ?? '18:00');
  const [durationMin, setDurationMin] = useState(String(slot?.durationMin ?? 60));
  const [capacity, setCapacity] = useState(slot?.maxCapacity ? String(slot.maxCapacity) : '');
  const [teacherId, setTeacherId] = useState(slot?.teacherId ?? '');
  const [teachers, setTeachers] = useState<TeacherListRow[]>(() => slot?.teachers ?? []);
  // Fix wave — `PUT /api/slots/:id` now runs the same teacher-time conflict
  // check as create (409 `SLOT_TIME_CONFLICT`): editing a slot's day/time
  // (or its teacher) onto another ACTIVE slot the same teacher already
  // holds is rejected. Tint the offending fields + show the server's own
  // message, same pattern as `ClassFormSheet`'s per-row conflict handling.
  const [conflictError, setConflictError] = useState<string | null>(null);

  const activeBranches = useMemo(() => (allBranches ?? []).filter((b) => !b.archivedAt), [allBranches]);
  // The one archived branch that may still legitimately appear: this slot's own.
  const ownBranchId = slot?.branchId ?? null;
  const ownArchivedBranch = useMemo(
    () => (ownBranchId ? (allBranches ?? []).find((b) => b.id === ownBranchId && !!b.archivedAt) : undefined),
    [allBranches, ownBranchId],
  );
  // An org with no cơ sở at all still sees no branch field — but a slot sitting
  // on an archived one does, or its branch would vanish from the form.
  //
  // THI-172: a dormant (non-Center) org gets the same "no branch field" answer
  // even when it has active branches — the field is chrome the plan gated,
  // not a fact about the org's data. Fails CLOSED (`?? false`) while the
  // subscription is still loading, same reasoning as useBranchScope.visible:
  // the field appearing and then disappearing reads as a bug, but appearing a
  // beat late on a Center org does not.
  const allowsBranches = subscription?.plan.allowsBranches ?? false;
  const showBranchField = (activeBranches.length > 0 || !!ownArchivedBranch) && allowsBranches;

  // Default to the acting member's own teacherId when it can be determined; otherwise
  // leave unset so the backend defaults to the acting membership.
  const defaultTeacherId = useMemo(
    () => members?.find((m) => m.user.id === authUser?.id)?.id ?? '',
    [members, authUser],
  );
  const effectiveTeacherId = teacherId || defaultTeacherId;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setConflictError(null);
    if (isEdit) {
      update.mutate(
        {
          dayOfWeek: Number(dayOfWeek), startTime, durationMin: Number(durationMin),
          teachers,
          ...(showBranchField ? { branchId: branchId || null } : {}),
        },
        {
          onSuccess: () => onOpenChange(false),
          onError: (err: Error & { code?: string }) => {
            if (err.code === 'SLOT_TIME_CONFLICT') setConflictError(err.message);
            // Any other error already toasts via useUpdateSlot's own onError.
          },
        },
      );
      return;
    }
    create.mutate(
      {
        subjectId, dayOfWeek: Number(dayOfWeek), startTime, durationMin: Number(durationMin),
        maxCapacity: capacity ? Number(capacity) : null,
        teachers,
        ...(showTeacher && effectiveTeacherId ? { teacherId: effectiveTeacherId } : {}),
        ...(showBranchField ? { branchId: branchId || null } : {}),
      },
      { onSuccess: (created) => { onOpenChange(false); onCreated?.(created); } },
    );
  };
  const canSubmit = subjectId && startTime && durationMin;
  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t(isEdit ? 'slot.edit' : 'slot.create')}</DialogTitle>
          {/* Edit mode only — a brand-new slot has no future/past sessions yet
              for the forward-push behaviour to be worth explaining. */}
          {isEdit && <DialogDescription>{t('slot.editDescription')}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2"><Label>{t('slot.subject')}</Label>
              <Select value={subjectId} onValueChange={setSubjectId} disabled={isEdit}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(subjects ?? []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            {/* Only when the org actually has branches — an org with none must
                see no new field. Editable on edit, unlike subject: moving a
                class to another cơ sở is a normal Tuesday, changing its subject
                is not. */}
            {showBranchField ? (
              <div className="grid gap-2"><Label>{t('slot.branch')}</Label>
                <Select value={branchId || NO_BRANCH} onValueChange={(v) => setBranchId(v === NO_BRANCH ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_BRANCH}>{t('slot.branchNone')}</SelectItem>
                    {activeBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    {/* Disabled: readable, keeps the trigger from going blank,
                        and cannot be chosen for another slot. Moving off it is
                        a one-way door on purpose — nothing may move ONTO an
                        archived cơ sở. */}
                    {ownArchivedBranch ? (
                      <SelectItem value={ownArchivedBranch.id} disabled>
                        {t('slot.branchArchived', { name: ownArchivedBranch.name })}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select></div>
            ) : null}
            {showTeacher ? (
              <div className="grid gap-2"><Label>{t('slot.teacherOwner')}</Label>
                <Select value={effectiveTeacherId} onValueChange={setTeacherId} disabled={isEdit}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {teacherOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                          {opt.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-2">
              <div className="grid gap-2"><Label>{t('slot.day')}</Label>
                <Select value={dayOfWeek} onValueChange={(v) => { setConflictError(null); setDayOfWeek(v); }}>
                  <SelectTrigger className={cn(conflictError && 'border-destructive ring-2 ring-destructive/15')}><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4,5,6,7].map((d) => <SelectItem key={d} value={String(d)}>{t(`days.${d}`)}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid gap-2"><Label>{t('slot.startTime')}</Label>
                <Input
                  type="time" value={startTime}
                  onChange={(e) => { setConflictError(null); setStartTime(e.target.value); }}
                  className={cn(conflictError && 'border-destructive ring-2 ring-destructive/15')}
                />
              </div>
              <div className="grid gap-2"><Label>{t('slot.duration')}</Label><Input type="number" min="15" step="15" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} /></div>
            </div>
            {conflictError && (
              <p className="m-0 flex items-start gap-1.5 text-[12.5px] text-destructive">
                <AlertCircle className="h-[13px] w-[13px] mt-[2px] shrink-0" />
                {conflictError}
              </p>
            )}
            {isEdit ? (
              // Sĩ số belongs to the class, one place to edit it (Johnny,
              // 2026-09-14): the khung giờ form only points there.
              <p className="m-0 text-xs text-surface-500">
                {t('slot.capacityOfClass', { count: slot?.maxCapacity ? String(slot.maxCapacity) : '∞' })}{' '}
                {slot?.group?.id ? <Link to={`/classes/${slot.group.id}`} className="text-brand-700 underline underline-offset-2">{t('slot.capacityOfClassLink')}</Link> : null}
              </p>
            ) : (
              <div className="grid gap-2"><Label>{t('slot.capacity')}</Label><Input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="∞" /></div>
            )}
            {/* Gated on showTeacher OR the override affordance itself — an org
                with teacher-assignment display off still needs a way to reach
                a migrated per-teacher override (the only place one can be set
                or edited). The roster half of the editor tags along in that
                case too; that's an acceptable side effect, not a new surface. */}
            {(showTeacher || (!!subscription?.plan.allowsPayroll && canManageMembers)) && (
              <div className="grid gap-2">
                <Label>{t('slot.teacherList')}</Label>
                <TeacherListEditor
                  ownerTeacherId={effectiveTeacherId}
                  rows={teachers}
                  onChange={setTeachers}
                  options={teacherOptions}
                  // Same plan gate the old override field carried: an org whose plan
                  // lacks payroll should not see a pay field it cannot act on. The
                  // roster half of the editor is NOT gated — a trợ giảng is a
                  // scheduling fact before it is a money one.
                  showOverride={!!subscription?.plan.allowsPayroll && canManageMembers}
                  mode="slot"
                  ns="scheduling"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('slot.cancel')}</Button>
            <Button type="submit" disabled={isPending || !canSubmit}>{t('slot.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
